/**
 * ChatInput Component
 * Phase 6.5 - Chat Conversationnel (CFO Virtuel)
 */

import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Send, Loader2, Sparkles } from 'lucide-react';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

// Quick prompts for common questions
const QUICK_PROMPTS = [
  { label: 'Factures impayees', prompt: 'Quelles sont mes factures impayees ?' },
  { label: 'Tresorerie', prompt: 'Quel est l\'etat de ma tresorerie ?' },
  { label: 'Score sante', prompt: 'Quel est mon score de sante financiere ?' },
  { label: 'Recommandations', prompt: 'Quelles sont tes recommandations ?' },
];

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  isLoading = false,
  disabled = false,
  placeholder = 'Posez votre question au CFO virtuel...',
}) => {
  const [message, setMessage] = useState('');
  const [showQuickPrompts, setShowQuickPrompts] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        120
      )}px`;
    }
  }, [message]);

  const handleSend = () => {
    if (!message.trim() || isLoading || disabled) return;
    onSend(message.trim());
    setMessage('');
    setShowQuickPrompts(false);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    onSend(prompt);
    setShowQuickPrompts(false);
  };

  return (
    <div className="space-y-3">
      {/* Quick prompts - only show when no messages sent yet */}
      {showQuickPrompts && (
        <div className="flex flex-wrap gap-2">
          {QUICK_PROMPTS.map((item, idx) => (
            <button
              key={idx}
              onClick={() => handleQuickPrompt(item.prompt)}
              disabled={isLoading || disabled}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                bg-white border border-gray-200 text-gray-600 rounded-full
                hover:bg-teal-50 hover:border-teal-200 hover:text-teal-700
                transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-3 h-3" />
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="relative flex items-end gap-2 bg-white rounded-2xl border border-gray-200 p-2 shadow-sm focus-within:border-teal-300 focus-within:ring-2 focus-within:ring-teal-100 transition-all duration-200">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isLoading || disabled}
          rows={1}
          className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-gray-800
            placeholder-gray-400 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed
            max-h-[120px] overflow-y-auto"
        />

        <button
          onClick={handleSend}
          disabled={!message.trim() || isLoading || disabled}
          className={`flex-shrink-0 p-2.5 rounded-xl transition-all duration-200 ${
            message.trim() && !isLoading && !disabled
              ? 'bg-teal-600 text-white hover:bg-teal-700 shadow-sm hover:shadow'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          title="Envoyer (Entree)"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Helper text */}
      <p className="text-[10px] text-gray-400 text-center">
        Entree pour envoyer, Shift+Entree pour retour a la ligne
      </p>
    </div>
  );
};

export default ChatInput;
